import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";

import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import { DataGrid } from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

export const MisJornadasFinalizadas = () => {
  const { auth } = useAuth();
  const toast = useToast();

  const [jornadas, setJornadas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [filtroCliente, setFiltroCliente] = useState(""); // centro_id
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAnio, setFiltroAnio] = useState("");
  const [busquedaTexto, setBusquedaTexto] = useState("");

  const [cargando, setCargando] = useState(true);

  // DataGrid paginación
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Orden controlada (mantenemos tu idea)
  const [orden, setOrden] = useState({ campo: "inicio", ascendente: true });

  // --------------------------
  // Helpers
  // --------------------------
  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const calcularDuracionMs = (inicio, fin) => {
    const a = parseDateSafe(inicio)?.getTime();
    const b = parseDateSafe(fin)?.getTime();
    if (!a || !b) return 0;
    return b - a;
  };

  const calcularDuracionTxt = (ms) => {
    if (!ms || isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  const formatearDuracion = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}min`;
  };

  // Normalizador para búsqueda tolerante a acentos/mayúsculas
  const normaliza = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  // --------------------------
  // Carga
  // --------------------------
  useEffect(() => {
    const fetchDatos = async () => {
      setCargando(true);
      try {
        const [resJ, resU, resCentros] = await Promise.all([
          fetch(`${Global.url}jornadas/finalizadas/mias`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}usuarios`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}centros/todos`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        if (!resJ.ok || !resU.ok || !resCentros.ok) {
          toast.error("Error al obtener datos.");
          return;
        }

        const [dataJornadas, dataUsuarios, dataCentros] = await Promise.all([
          resJ.json(),
          resU.json(),
          resCentros.json(),
        ]);

        setUsuarios(dataUsuarios);
        setCentros(dataCentros);

        const usuariosById = new Map(dataUsuarios.map((u) => [String(u.id), u]));

        const normalizadas = (dataJornadas || []).map((j) => {
          const admin = usuariosById.get(String(j.creado_por_admin_id));
          const duracionMs = calcularDuracionMs(j.inicio, j.fin);

          return {
            ...j,
            duracionMs,
            modificado_por_nombre: admin ? `${admin.nombre} ${admin.apellidos || ""}`.trim() : null,
          };
        });

        setJornadas(normalizadas);
      } catch (error) {
        console.error("Error en la conexión:", error);
        toast.error("No se pudo conectar con el servidor.");
      } finally {
        setCargando(false);
      }
    };

    fetchDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------
  // Centros map + opciones
  // --------------------------
  const mapaCentros = useMemo(() => {
    const map = {};
    centros.forEach((c) => {
      map[c.id] = c.nombre;
    });
    return map;
  }, [centros]);

  const centrosOrdenados = useMemo(() => {
    return centros
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const centroSeleccionado =
    centrosOrdenados.find((c) => String(c.id) === String(filtroCliente)) || null;

  // --------------------------
  // Filtrado
  // --------------------------
  const q = normaliza(busquedaTexto);

  const jornadasFiltradas = useMemo(() => {
    return (jornadas || []).filter((j) => {
      const fechaJornada = parseDateSafe(j.inicio);
      if (!fechaJornada) return false;

      const anio = String(fechaJornada.getFullYear());
      const mes = String(fechaJornada.getMonth() + 1).padStart(2, "0");

      const coincideUsuario = String(j.usuario_id) === String(auth?.id);
      const coincideCentro = !filtroCliente || Number(j.centro_id) === Number(filtroCliente);

      const desdeValida = !fechaDesde || fechaJornada >= new Date(fechaDesde);
      const hastaValida =
        !fechaHasta ||
        fechaJornada <
          new Date(new Date(fechaHasta).setDate(new Date(fechaHasta).getDate() + 1));

      const coincideAnio = !filtroAnio || anio === filtroAnio;
      const coincideMes = !filtroMes || mes === filtroMes;

      const centroNombre = mapaCentros[j.centro_id] || "";
      const coincideTexto =
        !q ||
        normaliza(centroNombre).includes(q) ||
        normaliza(j.observaciones_admin).includes(q) ||
        normaliza(j.motivo_fin).includes(q) ||
        normaliza(j.modificado_por_nombre).includes(q);

      return (
        coincideUsuario &&
        coincideCentro &&
        desdeValida &&
        hastaValida &&
        coincideAnio &&
        coincideMes &&
        coincideTexto
      );
    });
  }, [
    jornadas,
    auth?.id,
    filtroCliente,
    fechaDesde,
    fechaHasta,
    filtroAnio,
    filtroMes,
    q,
    mapaCentros,
  ]);

  // --------------------------
  // Ordenación (controlada)
  // --------------------------
  const jornadasOrdenadas = useMemo(() => {
    const arr = [...jornadasFiltradas];

    arr.sort((a, b) => {
      let valorA = a[orden.campo];
      let valorB = b[orden.campo];

      if (orden.campo === "centro") {
        valorA = mapaCentros[a.centro_id] || "";
        valorB = mapaCentros[b.centro_id] || "";
      }

      if (orden.campo === "inicio" || orden.campo === "fin") {
        valorA = parseDateSafe(valorA)?.getTime() ?? 0;
        valorB = parseDateSafe(valorB)?.getTime() ?? 0;
      }

      if (typeof valorA === "string") valorA = valorA.toLowerCase();
      if (typeof valorB === "string") valorB = valorB.toLowerCase();

      valorA = valorA ?? "";
      valorB = valorB ?? "";

      if (valorA < valorB) return orden.ascendente ? -1 : 1;
      if (valorA > valorB) return orden.ascendente ? 1 : -1;
      return 0;
    });

    return arr;
  }, [jornadasFiltradas, orden, mapaCentros]);

  // total trabajado
  const totalMs = useMemo(
    () => jornadasFiltradas.reduce((acc, j) => acc + (j.duracionMs || 0), 0),
    [jornadasFiltradas]
  );

  // reset page si cambian filtros
  useEffect(() => setPage(0), [filtroCliente, fechaDesde, fechaHasta, filtroMes, filtroAnio, busquedaTexto]);

  // --------------------------
  // Export PDF (igual lógica, sin alert)
  // --------------------------
  const calcularRango = () => {
    if (fechaDesde && fechaHasta) return { from: fechaDesde, to: fechaHasta };

    const hoy = new Date();
    const y = filtroAnio ? Number(filtroAnio) : hoy.getFullYear();

    if (filtroMes) {
      const m = Number(filtroMes) - 1; // 0..11
      const from = new Date(y, m, 1);
      const to = new Date(y, m + 1, 0);
      const fmt = (d) => d.toISOString().slice(0, 10);
      return { from: fmt(from), to: fmt(to) };
    }

    if (filtroAnio) {
      const from = new Date(y, 0, 1);
      const to = new Date(y, 11, 31);
      const fmt = (d) => d.toISOString().slice(0, 10);
      return { from: fmt(from), to: fmt(to) };
    }

    if (jornadasFiltradas.length > 0) {
      const minMs = Math.min(...jornadasFiltradas.map((j) => parseDateSafe(j.inicio)?.getTime() ?? Infinity));
      const maxMs = Math.max(...jornadasFiltradas.map((j) => parseDateSafe(j.fin)?.getTime() ?? 0));
      const fmt = (ms) => new Date(ms).toISOString().slice(0, 10);
      return { from: fmt(minMs), to: fmt(maxMs) };
    }

    return null;
  };

  const handleExportPDF = async () => {
    try {
      const rango = calcularRango();
      if (!rango) {
        toast.warning("Indica al menos un rango de fechas (Desde/Hasta) o selecciona Mes/Año.");
        return;
      }

      const params = new URLSearchParams();
      params.set("from", rango.from);
      params.set("to", rango.to);
      if (filtroCliente) params.set("centro_id", String(filtroCliente));
      if (busquedaTexto) params.set("query", busquedaTexto.trim());
      if (filtroMes) params.set("mes", filtroMes);
      if (filtroAnio) params.set("anio", filtroAnio);

      const url = `${Global.url}jornadas/finalizadas/mias/export.pdf?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "No se pudo generar el PDF");
      }

      const blob = await res.blob();
      const nombreCentro = filtroCliente
        ? mapaCentros[Number(filtroCliente)] || `centro-${filtroCliente}`
        : "todos";
      const fileName = `parte-jornadas-${auth?.id || "me"}-${nombreCentro}-${rango.from}_${rango.to}.pdf`;

      const urlBlob = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlBlob;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(urlBlob);

      toast.success("PDF generado correctamente.");
    } catch (e) {
      console.error(e);
      toast.error("Error al generar el PDF.");
    }
  };

  // --------------------------
  // DataGrid rows/cols
  // --------------------------
  const rows = useMemo(() => {
    return jornadasOrdenadas.map((j) => ({
      ...j,
      id: j.id, // DataGrid necesita id
      centro_nombre: mapaCentros[j.centro_id] || `Centro ${j.centro_id}`,
      inicio_txt: fmtDate(j.inicio),
      fin_txt: fmtDate(j.fin),
      duracion_txt: calcularDuracionTxt(j.duracionMs),
      motivo_txt: (j.observaciones_admin || j.motivo_fin || "-")?.toString(),
      modificado_txt: j.modificado_por_nombre || "-",
    }));
  }, [jornadasOrdenadas, mapaCentros]);

  const columns = useMemo(
    () => [
      { field: "centro_nombre", headerName: "Centro", flex: 1, minWidth: 240, sortable: false },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 170, sortable: false },
      { field: "fin_txt", headerName: "Fin", minWidth: 170, sortable: false },
      { field: "duracion_txt", headerName: "Duración", minWidth: 130, sortable: false },
      { field: "modificado_txt", headerName: "Modificado por", minWidth: 190, sortable: false },
      { field: "motivo_txt", headerName: "Motivo", flex: 1, minWidth: 260, sortable: false },
    ],
    []
  );

  // Orden clickable desde header (similar a tu tabla)
  const onHeaderClick = (field) => {
    // mapeo de columnas visibles a campos del orden
    const map = {
      centro_nombre: "centro",
      inicio_txt: "inicio",
      fin_txt: "fin",
      duracion_txt: "duracionMs",
      modificado_txt: "modificado_por_nombre",
      motivo_txt: "observaciones_admin",
    };

    const campoOrden = map[field] || field;
    setOrden((prev) => ({
      campo: campoOrden,
      ascendente: prev.campo === campoOrden ? !prev.ascendente : true,
    }));
  };

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Mis Jornadas Finalizadas
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "end",
              // 1 columna en móvil, 2 en tablet, 6 en escritorio
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(6, 1fr)",
              },
            }}
          >
            {/* Centro (escribible) */}
            <Autocomplete
              size="small"
              options={centrosOrdenados}
              getOptionLabel={(c) => c?.nombre || ""}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={centroSeleccionado}
              onChange={(_, newValue) => setFiltroCliente(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Centro"
                  placeholder="Escribe para buscar…"
                  fullWidth
                />
              )}
              clearOnEscape
              sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }} // ocupa 2 columnas en desktop
            />

            {/* Desde */}
            <TextField
              size="small"
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
            />

            {/* Hasta */}
            <TextField
              size="small"
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
            />

            {/* Año */}
            <FormControl size="small" fullWidth sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}>
              <InputLabel>Año</InputLabel>
              <Select label="Año" value={filtroAnio} onChange={(e) => setFiltroAnio(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 16 }, (_, i) => {
                  const year = 2025 + i;
                  return (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Mes */}
            <FormControl size="small" fullWidth sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}>
              <InputLabel>Mes</InputLabel>
              <Select label="Mes" value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                    {new Date(0, i).toLocaleString("es-ES", { month: "long" })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Buscar (siempre a lo ancho) */}
            <TextField
              size="small"
              label="Buscar"
              value={busquedaTexto}
              onChange={(e) => setBusquedaTexto(e.target.value)}
              placeholder="Centro, motivo o modificado por"
              fullWidth
              sx={{ gridColumn: "1 / -1" }}
            />
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 620, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={cargando}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            onColumnHeaderClick={(params) => onHeaderClick(params.field)}
            sx={{
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
              "& .MuiDataGrid-columnHeader": { alignItems: "center" },
              "& .MuiDataGrid-cellContent": { width: "100%" },
            }}
          />
        </Paper>

        {/* Total */}
        <Paper sx={{ p: 2 }}>
          <Typography sx={{ textAlign: "right" }}>
            <strong>Total trabajado en este listado:</strong> {formatearDuracion(totalMs)}
          </Typography>
        </Paper>

        {/* Export PDF */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={cargando || jornadasFiltradas.length === 0}
            title={
              jornadasFiltradas.length === 0
                ? "No hay filas que exportar con los filtros actuales"
                : "Exportar parte (PDF)"
            }
          >
            Exportar parte (PDF)
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
